import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Grid, GridColumn, GridRow, Icon, Label } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan } from "components";
import ENV from "config/Env";
import { usePermanentRealTimeUser } from "hooks";
import { TYRETEAM_CHANNELS, TYRE_LOCATIONS, TYRE_REPLACEMENT_SOURCES, VEHICLE_TYRES_SOURCES } from "models";
import {
  ReplacementOfferModal,
  ReplacementOrderModal,
  TyreReplacementTableRow,
  TyreTableRow
} from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components";
import { useTyreNet, useTyreReplacementUpdateMutation, useTyreTeam } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { getAxleName, getSpareIndex, sortAndGroupByAxle } from "modules/AppointmentDetails/components/VehicleTyres/utils";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { DBBLabelsModal } from "modules/CarDetails/components/Tyres/components";
import { convertVehicleTyresToDBBLabels } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";
import { isCarReadyOrQualityCheckDoneOrCanceled } from "utils/appointmentUtils";
import { formatDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { Appointment, VehicleTyre, VehicleTyreReplacement } from "models";
import type { SemanticWIDTHS } from "semantic-ui-react";

type TyreTableProps = {
  appointment: Appointment;
  tyres: VehicleTyre[];
  tyreLocation: TYRE_LOCATIONS;
  readOnly?: boolean;
};

const openOrderPortal = (channel: TYRETEAM_CHANNELS | null) => {
  if (!channel) return;

  if (channel === TYRETEAM_CHANNELS.WheelTyre) window.open(ENV.wheelTireOrderPortalURL, "_blank");
  else window.open(ENV.bandenExpressOrderPortalURL, "_blank");
};

const getName = (lastUpdatedTyre: VehicleTyre, t: TFunction) => {
  if (lastUpdatedTyre.source === VEHICLE_TYRES_SOURCES.DBB) return "DBB";
  if (lastUpdatedTyre.source === VEHICLE_TYRES_SOURCES.Scanner) return t("v8_tyre_scanner", "Tyre Scanner");
  if (lastUpdatedTyre.updated_by) return lastUpdatedTyre.updated_by.last_name + " " + lastUpdatedTyre.updated_by.first_name;

  return t("v8_unknown", "Unknown");
};

export const TyreTable = ({ appointment, tyres, tyreLocation, readOnly }: TyreTableProps) => {
  const { t } = useTranslation();

  const [showDBBModal, setShowDBBModal] = useState(false);
  const [offerModalVehicleTyre, setOfferModalVehicleTyre] = useState<VehicleTyre | null>(null);
  const [showModalTyreReplacement, setShowOrderModalTyreReplacement] = useState(false);

  const appointmentLocation = useAppointmentLocation();
  const canUpdateTyreReplacements = useCan("update", "appointments") && !isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier);
  const { user } = usePermanentRealTimeUser();
  const tyreReplacementMutation = useTyreReplacementUpdateMutation();
  const tyreTeam = useTyreTeam(appointmentLocation);
  const tyreNet = useTyreNet();

  const handleOrderReplacement = (replacement: VehicleTyreReplacement) => {
    if (!user?.tyre_ordering_enabled) return;

    if (replacement.order_placed && replacement.source === TYRE_REPLACEMENT_SOURCES.TyreNet) return window.open("https://tic.tyrenet.nl", "_blank");
    if (replacement.order_placed && replacement.tyre_team_channel) return openOrderPortal(replacement.tyre_team_channel);

    setShowOrderModalTyreReplacement(true);
  };

  const tyreColumns: { header: string; width: SemanticWIDTHS }[] = [
    { header: t("v8_position", "Position"), width: 2 },
    { header: t("v8_size", "Size"), width: 2 },
    { header: t("v8_type", "Type"), width: 4 },
    { header: t("v8_brand", "Brand"), width: 1 },
    { header: t("v8_profile", "Profile"), width: 1 },
    { header: t("v8_price", "Price"), width: 1 },
    { header: t("v8_remarks", "Remarks"), width: 4 },
    { header: "", width: 2 }
  ];

  let title = t("v8_on_car", "On Car").toUpperCase();
  let icon = "car mirrors";
  if (!appointment.car || (!appointment.car.is_truck && appointment.car.tyres?.length && appointment.car.tyres[0].axle > 0)) {
    title = t("v8_on_vehicle", "On Vehicle").toUpperCase();
    icon = "truck";
  } else if (appointment.car.is_truck) {
    title = t("v8_on_truck", "On Truck").toUpperCase();
    icon = "truck";
  }

  const { getDeliveryChannelName, getDeliveryPopup } = appointmentLocation?.is_tyre_team_enabled ? tyreTeam : tyreNet;

  const tyresByAxle = sortAndGroupByAxle(tyres);
  const truckSpareIndex = getSpareIndex(tyresByAxle);
  const dbbTyre = tyres.find(t => t.dbb_location_name);

  const lastUpdatedTyre = tyres.sort((a, b) => b.updated_on.localeCompare(a.updated_on))[0];
  const name = getName(lastUpdatedTyre, t);
  const updated_by = t("v8_updated_by_{{name}}_on_{{date}}", "Updated by {{name}} on {{date}}")
    .replace("{{name}}", name)
    .replace("{{date}}", formatDate(lastUpdatedTyre.updated_on, DATE_FORMATS_FNS.dateMonthYearTime));

  return (
    <div className="TyreTable">
      <div className="tyre-title-container">
        <div className="tyre-title">
          {<Icon className={icon} />}
          <p>{title}</p>
          <Label basic>{updated_by}</Label>
        </div>
        {!readOnly && (
          <div className="tyre-title__wrapper">
            {tyreLocation === TYRE_LOCATIONS.InStorage && dbbTyre && (
              <>
                <span className="tyre-title__wrapper__storage">{`${t("v8_storage_location", "Storage location")}: `}</span>
                <span className="tyre-title__wrapper__locationName">{dbbTyre.dbb_location_name}</span>
              </>
            )}
            <Button basic className="light" size="tiny" onClick={() => setShowDBBModal(true)}>
              <p>{t("v8_tire_labels", "Tire Labels")}</p>
            </Button>
          </div>
        )}
      </div>
      <Grid columns={16}>
        <GridRow className="header">
          {tyreColumns.map(item => (
            <GridColumn key={item.header} width={item.width}>
              <p>{item.header}</p>
            </GridColumn>
          ))}
        </GridRow>
        {Array.from(tyresByAxle, ([axle, tyres]) => (
          <React.Fragment key={"axle-" + axle}>
            {axle > 0 && tyres.length > 0 && (
              <GridRow className="axle-header">
                <GridColumn width={16}>
                  <p>{getAxleName(tyres[0], truckSpareIndex, t)}</p>
                </GridColumn>
              </GridRow>
            )}
            {tyres.map(tyre => (
              <React.Fragment key={tyre.id}>
                <TyreTableRow vehicleTyre={tyre} onRequestReplacement={setOfferModalVehicleTyre} appointment={appointment} readOnly={readOnly} />
                {tyre.replacements
                  ?.filter(r => !r.mounted)
                  .map(replacement => (
                    <TyreReplacementTableRow
                      key={replacement.id}
                      vehicleTyre={tyre}
                      replacement={replacement}
                      appointment={appointment}
                      location={appointmentLocation}
                      onUpdateReplacement={tyreReplacementMutation.mutate}
                      onOrderReplacement={handleOrderReplacement}
                      getDeliveryChannelName={getDeliveryChannelName}
                      getDeliveryPopup={getDeliveryPopup}
                      canUpdateAppointment={!readOnly && canUpdateTyreReplacements}
                      isTyreNetEnabled={appointmentLocation?.is_tyre_net_enabled ?? false}
                      isTyreTeamEnabled={appointmentLocation?.is_tyre_team_enabled ?? false}
                      tyreOrderingEnabled={user?.tyre_ordering_enabled ?? false}
                    />
                  ))}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Grid>
      {!readOnly && (
        <>
          <DBBLabelsModal
            open={showDBBModal}
            onClose={() => setShowDBBModal(false)}
            tyreLabels={convertVehicleTyresToDBBLabels(tyres)}
            appointment={appointment}
            car={appointment.car}
          />
          <ReplacementOfferModal
            appointment={appointment}
            location={appointmentLocation}
            existingReplacements={tyres.flatMap(tyre => tyre.replacements ?? [])}
            tyre={offerModalVehicleTyre}
            onRequestClose={() => setOfferModalVehicleTyre(null)}
          />
          <ReplacementOrderModal
            open={showModalTyreReplacement}
            existingReplacements={tyres.flatMap(tyre => tyre.replacements ?? [])}
            location={appointmentLocation}
            appointment={appointment}
            onClose={() => setShowOrderModalTyreReplacement(false)}
          />
        </>
      )}
    </div>
  );
};
